import { useMemo, useState } from 'react';
import { has } from 'lodash-es';
import { useSelector, useDispatch } from 'umi';
import { useMount } from 'ahooks';
import { parseListString } from '@intelligence/v3/utils/common';
import { GenreTagClickType } from '@intelligence/v3/utils/genre';
import { COMMON_JOINER, generateDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { FILTER_NAMESPACE } from '@intelligence/models/filter';
import * as filterKeys from '@intelligence/components/Filter/FilterBase/_config';
import LOCALE_EN from '@intelligence/_locales/en-US/common';
import LOCALE_CN from '@intelligence/_locales/zh-CN/common';

import type { ICommonOptionCategory } from '@intelligence/types/common';
import type { ISensorTowerTag } from '@intelligence/types/topCharts';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

export const SENTOWERTAG_MAP: IRecord<string> = {
  'Art Style': filterKeys.SENSOR_TOWER_TAG_ART_STYLE,
  'Camera POV': filterKeys.SENSOR_TOWER_TAG_CAMERA_POV,
  Monetization: filterKeys.SENSOR_TOWER_TAG_MONETIZATION,
  'Product Model': filterKeys.SENSOR_TOWER_TAG_PRODUCT_MODEL,
  Setting: filterKeys.SENSOR_TOWER_TAG_SETTING,
  Tags: filterKeys.SENSOR_TOWER_TAG_TAGS, // 暂不确定是tags还是tag
  Tag: filterKeys.SENSOR_TOWER_TAG_TAGS,
  Theme: filterKeys.SENSOR_TOWER_TAG_THEME,
};

export const getSensorTowerTagCategoryFilterKey = (value: string): string => {
  if (!value) {
    return '';
  }

  const keys = Object.keys(SENTOWERTAG_MAP);
  let result = '';

  for (const key of keys) {
    if (key === value) {
      result = SENTOWERTAG_MAP[key];
      break;
    }
  }

  return result;
};

// 递归tag
const formatSensorTowerTagsForTagCardContext = (
  tags: ISensorTowerTag[],
  result: ICommonOptionCategory[],
  parentValue?: string,
): ICommonOptionCategory[] => {
  // 这里length为0时，不能返回空数组，因为这里是需要递归往result数组push数据的，递归的开始result会传入空数组
  if (!Array.isArray(result)) {
    return [];
  }

  if (!Array.isArray(tags) || tags.length === 0) {
    return result;
  }

  for (const tag of tags) {
    const tagName = tag?.tag_name || '';
    const tagValue = parentValue ? generateDataSourceMetricKey(parentValue, tagName, COMMON_JOINER) : tagName;
    const subTags = tag?.Tags;

    if (Array.isArray(subTags) && subTags.length > 0) {
      formatSensorTowerTagsForTagCardContext(subTags, result, tagValue);
    } else {
      result.push({
        value: tagValue,
        text: tagName,
        textEN: tagName,
        textCN: tag?.tag_name_ch,
      });
    }
  }

  return result || [];
};

const generateTags = (
  hasGenres: boolean,
  genre: string,
  genreOption?: IOptionWithChildren | null,
): ICommonOptionCategory[] => {
  if (!hasGenres) {
    return [];
  }

  return [
    {
      value: genre,
      text: genre,
      textEN: genre,
      textCN: genreOption?.textCN || genre,
    },
  ];
};

const generateTagsMultiple = (
  hasChildGenres: boolean,
  parentGenre: string,
  childGenres: string[],
  childGenreOptions?: IOptionWithChildren[] | null,
): ICommonOptionCategory[] => {
  if (!hasChildGenres) {
    return [];
  }

  return childGenres.map((childGenre) => {
    const firstChildOption = childGenreOptions?.find((option) => option?.value === childGenre);
    return {
      parentValue: parentGenre,
      value: childGenre,
      text: childGenre,
      textEN: childGenre,
      textCN: firstChildOption?.textCN || childGenre,
    };
  });
};

/**
 * 构建主、副品类的tag标签数据
 * @param params - 参数对象
 * @return 主、副品类的tag标签数据
 */
export const formatGenreTagsForTagCard = (params: {
  definitionsLoading?: boolean;
  genreOptions?: IOptionWithChildren[] | null;
  superGenreString?: string;
  majorGenreString?: string;
}): ICommonOptionCategory[] => {
  const { definitionsLoading, genreOptions, superGenreString, majorGenreString } = params || {};

  // 解析各层品类数据
  const superGenres = parseListString(superGenreString);
  const majorGenres = parseListString(majorGenreString);

  const hasSuperGenres = Array.isArray(superGenres) && superGenres.length > 0;
  const hasMajorGenres = Array.isArray(majorGenres) && majorGenres.length > 0;

  // 获取各层的 option 数据
  const firstSuperGenre = hasSuperGenres ? superGenres[0] : '';
  const firstSuperGenreOption = (() => {
    if (definitionsLoading || !Array.isArray(genreOptions) || genreOptions.length === 0 || !firstSuperGenre) {
      return null;
    }
    return genreOptions.find((option) => option?.value === firstSuperGenre);
  })();

  const majorGenreOptions = firstSuperGenreOption?.children;

  const superGenreTags = generateTags(hasSuperGenres, firstSuperGenre, firstSuperGenreOption);
  const majorGenreTags = generateTagsMultiple(hasMajorGenres, firstSuperGenre, majorGenres, majorGenreOptions);

  // 固定返回2个一级option
  const tags: ICommonOptionCategory[] = [
    // Main Genre
    {
      value: GenreTagClickType.SuperGenre,
      text: 'Main Genre',
      textEN: 'Main Genre',
      textCN: '主品类',
      children: superGenreTags,
    },
    // Sub Genre
    {
      value: GenreTagClickType.MajorGenre,
      text: 'Sub Genre',
      textEN: 'Sub Genre',
      textCN: '副品类',
      children: majorGenreTags,
    },
  ];

  return tags;
};

/**
 * 构建App Magic品类的tag标签数据
 * @param params - 参数对象
 * @return App Magic品类的tag标签数据
 */
export const formatAppMagicGenreTagsForTagCard = (params: {
  definitionsLoading?: boolean;
  genreOptions?: IOptionWithChildren[] | null;
  superGenreString?: string;
  majorGenreString?: string;
  midGenreString?: string;
  subGenreString?: string;
  minorGenreString?: string;
}): ICommonOptionCategory[] => {
  const {
    definitionsLoading,
    genreOptions,
    superGenreString,
    majorGenreString,
    midGenreString,
    subGenreString,
    minorGenreString,
  } = params || {};

  // 解析各层品类数据
  const superGenres = parseListString(superGenreString);
  const majorGenres = parseListString(majorGenreString);
  const midGenres = parseListString(midGenreString);
  const subGenres = parseListString(subGenreString);
  const minorGenres = parseListString(minorGenreString);

  const hasSuperGenres = Array.isArray(superGenres) && superGenres.length > 0;
  const hasMajorGenres = Array.isArray(majorGenres) && majorGenres.length > 0;
  const hasMidGenres = Array.isArray(midGenres) && midGenres.length > 0;
  const hasSubGenres = Array.isArray(subGenres) && subGenres.length > 0;
  const hasMinorGenres = Array.isArray(minorGenres) && minorGenres.length > 0;

  // 获取各层的 option 数据
  const firstSuperGenre = hasSuperGenres ? superGenres[0] : '';
  const firstSuperGenreOption = (() => {
    if (definitionsLoading || !Array.isArray(genreOptions) || genreOptions.length === 0 || !firstSuperGenre) {
      return null;
    }
    return genreOptions.find((option) => option?.value === firstSuperGenre);
  })();

  const firstMajorGenre = hasMajorGenres ? majorGenres[0] || '' : '';
  const majorGenreOptions = firstSuperGenreOption?.children;
  const firstMajorGenreOption = majorGenreOptions?.find((option) => option?.value === firstMajorGenre);

  const firstMidGenre = hasMidGenres ? midGenres[0] || '' : '';
  const midGenreOptions = firstMajorGenreOption?.children;
  const firstMidGenreOption = midGenreOptions?.find((option) => option?.value === firstMidGenre);

  const firstSubGenre = hasSubGenres ? subGenres[0] || '' : '';
  const subGenreOptions = firstMidGenreOption?.children;
  const firstSubGenreOption = subGenreOptions?.find((option) => option?.value === firstSubGenre);

  const minorGenreOptions = firstSubGenreOption?.children;

  const superGenreTags = generateTags(hasSuperGenres, firstSuperGenre, firstSuperGenreOption);
  const majorGenreTags = generateTags(hasMajorGenres, firstMajorGenre, firstMajorGenreOption);
  const midGenreTags = generateTags(hasMidGenres, firstMidGenre, firstMidGenreOption);
  const subGenreTags = generateTagsMultiple(hasSubGenres, firstMidGenre, subGenres, subGenreOptions);
  const minorGenreTags = generateTagsMultiple(hasMinorGenres, firstSubGenre, minorGenres, minorGenreOptions);

  // 固定返回5个一级option
  const tags: ICommonOptionCategory[] = [
    // Super Genre
    {
      value: GenreTagClickType.SuperGenre,
      text: 'Super Genre',
      textEN: LOCALE_EN.dbi_super_genre,
      textCN: LOCALE_CN.dbi_super_genre,
      children: superGenreTags,
    },
    // Level-1 Genre
    {
      value: GenreTagClickType.MajorGenre,
      text: 'Level-1 Genre',
      textEN: LOCALE_EN.dbi_level_1_genre,
      textCN: LOCALE_CN.dbi_level_1_genre,
      children: majorGenreTags,
    },
    // Level-2 Genre
    {
      value: GenreTagClickType.MidGenre,
      text: 'Level-2 Genre',
      textEN: LOCALE_EN.dbi_level_2_genre,
      textCN: LOCALE_CN.dbi_level_2_genre,
      children: midGenreTags,
    },
    // Level-3 Genre
    {
      value: GenreTagClickType.SubGenre,
      text: 'Level-3 Genre',
      textEN: LOCALE_EN.dbi_level_3_genre,
      textCN: LOCALE_CN.dbi_level_3_genre,
      children: subGenreTags,
    },
    // Level-4 Genre
    {
      value: GenreTagClickType.MinorGenre,
      text: 'Level-4 Genre',
      textEN: LOCALE_EN.dbi_level_4_genre,
      textCN: LOCALE_CN.dbi_level_4_genre,
      children: minorGenreTags,
    },
  ];

  return tags;
};

// 第一层是分类，所以不递归
export const formatSensorTowerTagsForTagCard = (tagCategories: ISensorTowerTag[]): ICommonOptionCategory[] => {
  if (!Array.isArray(tagCategories)) {
    return [];
  }

  return tagCategories.map((tagCategory) => {
    const tagCategoryValue = tagCategory?.tag_name;
    const tagCategoryTags = tagCategory?.Tags;

    return {
      value: tagCategoryValue,
      text: tagCategoryValue,
      textEN: tagCategoryValue,
      textCN: tagCategory?.tag_name_ch,
      children: tagCategoryTags ? formatSensorTowerTagsForTagCardContext(tagCategoryTags, []) : [],
    };
  });
};

const tranFormTag = (list: any[], parentValue?: string): any[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  return list.map((item: any) => {
    const itemTagName = item?.tag_name || '';

    return {
      value: parentValue ? `${parentValue}@${itemTagName}` : itemTagName,
      text: itemTagName,
      textCN: item?.tag_name_ch || '',
      children: tranFormTag(item?.Tags, itemTagName),
    };
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function useSenTowerTag(props: any) {
  const dispatch = useDispatch();

  const { senTowerTagFilter } = useSelector<any, any>((state) => {
    const { senTowerTagFilter } = state?.[FILTER_NAMESPACE] || {};
    return { senTowerTagFilter };
  });

  const [tagLoading, setTagLading] = useState(true);
  const [tagOptions, setTagOptions] = useState<any[]>([]);

  const tagConfig = useMemo<IRecord>(() => {
    if (!Array.isArray(tagOptions) || tagOptions.length === 0) {
      return {};
    }

    return tagOptions.reduce((result, item: any) => {
      const itemTagName = item?.tag_name || '';
      if (has(SENTOWERTAG_MAP, itemTagName)) {
        result[SENTOWERTAG_MAP[itemTagName]] = tranFormTag(item?.Tags);
      }
      return result;
    }, {});
  }, [tagOptions]);

  const getSenTowerTagList = async () => {
    if (!senTowerTagFilter?.loading && senTowerTagFilter?.data === null) {
      try {
        const res: any = await dispatch({
          type: `${FILTER_NAMESPACE}/getSenTowerTagList`,
          payload: {},
        });
        setTagOptions(res);
        setTagLading(false);
      } catch (err) {
        setTagLading(false);
      }
    } else {
      setTagLading(false);
      setTagOptions(senTowerTagFilter?.data);
    }
  };

  useMount(() => {
    void getSenTowerTagList();
  });

  return {
    tagLoading,
    tagOptions,
    tagConfig,
  };
}
